import { computed, inject, Injectable, signal } from '@angular/core';
import { effect } from '@angular/core';
import { FlowTypes, OrderFlowType, OrderStatusType, OrderV2, ProductStatusType, ProductTracker, ServiceType, StepStatusType } from '../../../../models/order/order.model';
import { lastValueFrom } from 'rxjs';
import { OrderApiCacheService } from './order-api-response/order-api-cache.service';
import { OOFProvidersEnum } from '../../../../enums/oofproviders.enum';
import * as Sentry from "@sentry/angular";

export const ORDER_STORAGE_KEY = 'ORDER_DATA';
// out of footprint providers OOF
const OOF_PROVIDERS = ['Earthlink', 'Consolidated', 'Brightspeed', 'Altafiber','HawaiianTelcom', 'Ziply', 'Charter', 'Spectrum', 'Optimum', 'Frontier', 'Viasat', 'Windstream','WOW'];
// Providers that submit the schedule date later (with the SO api call on order summary)
const SCHEDULE_DATE_DEFERRED_PROVIDERS = new Set<string>([
    OOFProvidersEnum.ZIPLY,
    OOFProvidersEnum.WOW,
    OOFProvidersEnum.EARTHLINK,
    OOFProvidersEnum.VIASAT,
    OOFProvidersEnum.HUGHESNET,
    OOFProvidersEnum.WINDSTREAM,
    OOFProvidersEnum.METRONET,
    OOFProvidersEnum.HAWAIIAN_TELECOM,
    OOFProvidersEnum.ALTAFIBER
]);
// Providers that render OptionalOffers as individual checkbox add-ons instead of a dropdown
const CHECKBOX_OPTIONAL_OFFER_PROVIDERS = new Set<string>([
    OOFProvidersEnum.VIASAT,
    OOFProvidersEnum.FRONTIER,
    OOFProvidersEnum.CONSOLIDATED,
    OOFProvidersEnum.BRIGHTSPEED,
    OOFProvidersEnum.WINDSTREAM,
    OOFProvidersEnum.ZIPLY,
    OOFProvidersEnum.EARTHLINK,
    OOFProvidersEnum.WOW,
]);

@Injectable({ providedIn: 'root' })
export class OrderStateServiceV2 {
    private orderService = inject(OrderApiCacheService);

    constructor() {
        effect(() => {
            const order = this._order();
            this.syncSentryOrderTag(order?.orderNumber);
            if (order) {
                this.saveToStorage();
            }
        });

        this.loadFromLocal();
    }

    private _order = signal<OrderV2 | null>(null);
    order = this._order.asReadonly();
    orderNumber = computed(() => this._order()?.orderNumber || null);
    isValidOrder = computed(() => !!this.orderNumber());
    currentFlowType = computed(() => this._order()?.flowType || null);
    isDraft = computed(() => this._order()?.status === OrderStatusType.Draft);
    isSubmitted = computed(() => this._order()?.status === OrderStatusType.Submitted);
    serviceStatuses = computed(() => {
        return this._order()?.serviceStatuses || [];
    });

    /** Clear all domain states */
    clearAll() {
        this._order.set(null);
        sessionStorage.removeItem(ORDER_STORAGE_KEY);
    }

    initOrder(orderNumber: string) {
        this._order.set({ orderNumber, status: OrderStatusType.Draft });
    }

    loadFromLocal() {
        const stored = sessionStorage.getItem(ORDER_STORAGE_KEY);
        if (stored) this._order.set(JSON.parse(stored));
    }

    loadFromOrder(order: OrderV2) {
        this._order.set(order);
    }

    updateOrder(partialData: Partial<OrderV2>) {
        if (!this.order()) return;
        this._order.set({
            ...this.order(),
            ...partialData,
            lastSavedAt: new Date().toISOString(),
            status: OrderStatusType.Draft
        })
    }

    markOrderAsSubmitted() {
        if (!this.order()) return;
        this._order.set({
            ...this.order(),
            status: OrderStatusType.Submitted,
            lastSavedAt: new Date().toISOString()
        })
    }

    /** Save the order data to the API */
    async saveOrderData() {
        if (this._order()) {
            await lastValueFrom(this.orderService.setOrderApiDataCache(this._order()));
        }
    }

    updateOrderFlowType(flowType: OrderFlowType) {
        if (!this.order()) return;
        this.updateOrder({ flowType });
    }

    findServicesStatus(serviceType: ServiceType): ProductTracker[] {
        return this.serviceStatuses().filter(status => status.key === serviceType);
    }

    getCompletedFlows(): ProductTracker[] {
        return this.serviceStatuses().filter(p => p.status === ProductStatusType.COMPLETED);
    }

    updateServiceStatus(serviceType: ServiceType, status: ProductStatusType) {
        const statuses = this.serviceStatuses();
        const serviceStatus = statuses.find(status => status.key === serviceType);
        if (serviceStatus) {
            this.updateOrder({
                serviceStatuses: statuses.map(serviceStatus =>
                    serviceStatus.key === serviceType
                        ? { ...serviceStatus, status: status }
                        : serviceStatus
                )
            });
        }
    }

    markCurrentFlowAsCompleted() {
        const currentFlow = this.currentFlowType();
        if (currentFlow) {
            if (currentFlow === OrderFlowType.Internet) {
                this.updateOrder({
                    internetFlow: {
                        ...this._order()?.internetFlow,
                        status: StepStatusType.Completed,
                        lastSavedAt: new Date().toISOString()
                    }
                })

                this.updateServiceStatus('INTERNET', ProductStatusType.COMPLETED);
            }

            if (currentFlow === OrderFlowType.Wireless) {
                this.updateOrder({
                    wirelessFlow: {
                        ...this._order()?.wirelessFlow,
                        status: StepStatusType.Completed,
                        lastSavedAt: new Date().toISOString()
                    }
                })

                this.updateServiceStatus('WIRELESS', ProductStatusType.COMPLETED);
            }

            if (currentFlow === OrderFlowType.Video) {
                this.updateOrder({
                    videoFlow: {
                        ...this._order()?.videoFlow,
                        status: StepStatusType.Completed,
                        lastSavedAt: new Date().toISOString()
                    }
                })

                this.updateServiceStatus('VIDEO', ProductStatusType.COMPLETED);
            }

            if (currentFlow === OrderFlowType.Voice) {
                this.updateOrder({
                    voiceFlow: {
                        ...this._order()?.voiceFlow,
                        status: StepStatusType.Completed,
                        lastSavedAt: new Date().toISOString()
                    }
                })

                this.updateServiceStatus('VOICE', ProductStatusType.COMPLETED);
            }
        }
    }

    private saveToStorage() {
        sessionStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(this.order()));
    }

    private syncSentryOrderTag(orderNumber: string | null | undefined) {
        Sentry.setTag('Order', orderNumber || undefined);
    }

    getProvider(): string {
        const flow = this.currentFlowType();
        if (!flow) return '';

        const order = this.order();

        switch (flow) {
            case FlowTypes.INTERNET:
                return order.internetFlow?.provider?.toLowerCase() === OOFProvidersEnum.CHARTER ? 'Spectrum' : order.internetFlow?.provider || '';

            case FlowTypes.WIRELESS:
                return order.wirelessFlow?.wirelessSetup?.selectedCustomerCode || '';

            case FlowTypes.VIDEO:
                return order.videoFlow?.provider || '';

            case FlowTypes.VOICE:
                return order.voiceFlow?.provider || '';
            default:
                throw new Error(`Unknown flow type: ${flow}`);
        }
    }


    isOOFProvider(): boolean {
        const provider = this.getProvider()?.toLowerCase();
        return OOF_PROVIDERS.some(p => p.toLowerCase() === provider);
    }

    isOOFFrontierProvider(): boolean {
        const provider = this.getProvider()?.toLowerCase();
        return provider === 'frontier';
    }

    isCheckboxOptionalOfferProvider(): boolean {
        const provider = this.getProvider()?.toLowerCase();
        return !!provider && CHECKBOX_OPTIONAL_OFFER_PROVIDERS.has(provider);
    }

    isOOFEarthlinkProvider(): boolean {
        const provider = this.getProvider()?.toLowerCase();
        return provider === OOFProvidersEnum.EARTHLINK;
    }

    isOOFConsolidatedProvider(): boolean {
        const provider = this.getProvider()?.toLowerCase();
        return provider === OOFProvidersEnum.CONSOLIDATED;
    }

    isOOFWindstreamProvider(): boolean {
        const provider = this.order()?.internetFlow?.provider?.toLowerCase();
        return provider === OOFProvidersEnum.WINDSTREAM;
    }

    isSubmitScheduleDateDeferred(): boolean {
        return SCHEDULE_DATE_DEFERRED_PROVIDERS.has(this.getProvider()?.toLowerCase());
    }
}
